/* ============================================================================
   Artisticases — predictive search (2026-09-16 search package)

   Owns the header search field (desktop dropdown) and the phone search sheet.
   Replaces the theme's search-ajax module on those two forms: the data
   attribute data-ac-predictive marks a form; the theme's js-search-ajax
   classes are not present on them, so its keyup handlers never fire.

   Behaviour:
   - the loading state shows on the FIRST keystroke (before the debounce), so
     the dropdown opens right away with a spinner instead of appearing after
     the response;
   - requests are debounced (250 ms) and stale responses are dropped by
     sequence number; in-flight requests are aborted;
   - Shopify's predictive endpoint (/search/suggest.json) with product type
     and price in the payload; five rows;
   - combobox semantics: ArrowUp/Down move the active row, Enter opens the
     active row or submits the form when none is active, Escape closes and
     keeps focus on the input, Home/End jump;
   - Clear (×) empties the field and closes; View all results submits the
     real query to /search.
   The phone sheet is the same markup inside a fixed full-screen panel
   (snippets/ac-search-sheet.liquid), opened by the header's search button.
   ========================================================================== */
(function () {
  'use strict';

  var DEBOUNCE = 250;
  var LIMIT = 5;
  var ENDPOINT = (window.theme && theme.routes && theme.routes.search_url ? theme.routes.search_url : '/search') + '/suggest.json';
  var SEARCH_URL = window.theme && theme.routes && theme.routes.search_url ? theme.routes.search_url : '/search';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function money(cents) {
    try {
      if (window.theme && typeof theme.Currency !== 'undefined' && theme.Currency.formatMoney) return theme.Currency.formatMoney(cents, theme.moneyFormat || '${{amount}}');
    } catch (e) { /* fall through */ }
    try {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') return Shopify.formatMoney(cents, (window.theme && theme.moneyFormat) || '${{amount}}');
    } catch (e) { /* fall through */ }
    return '$' + (cents / 100).toFixed(2);
  }

  /* The predictive API returns prices as strings in the shop's currency
     ("29.99"); the same helper the theme uses multiplies by 100 when no
     decimal point is present. */
  function toCents(v) {
    if (v === null || v === undefined) return null;
    var s = String(v);
    return s.indexOf('.') === -1 ? parseInt(s, 10) * 100 : Math.round(parseFloat(s) * 100);
  }

  /* Short display name, the collection-card rule: device and stock words removed */
  var STRIP = ['Personalized', 'Custom Name', 'Custom', 'iPhone Case', 'Galaxy Case', 'AirPods Case', 'Laptop Sleeve', 'Wireless Charger Pad', 'Wireless Charger', 'Desk Mat'];
  function shortName(title) {
    var t = title;
    STRIP.forEach(function (w) { t = t.replace(new RegExp(w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), ' '); });
    t = t.replace(/\s+/g, ' ').replace(/\s*-\s*$/, '').trim();
    return t || title;
  }
  var TYPE_LABEL = { 'iPhone Cases': 'iPhone Case', 'Galaxy Cases': 'Galaxy Case', 'AirPods Cases': 'AirPods Case', 'Laptop Sleeve': 'Laptop Sleeve', 'Wireless Charger': 'Wireless Charger', 'Desk Mat': 'Desk Mat' };

  function resize(src, w) {
    if (!src) return '';
    if (window.Shopify && typeof Shopify.resizeImage === 'function') return Shopify.resizeImage(src, w + 'x');
    return src;
  }

  function escapeHtml(s) { return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

  function setup(form) {
    if (form.dataset.acPredictiveReady === 'true') return;
    form.dataset.acPredictiveReady = 'true';

    var input = form.querySelector('input[type="search"], input[name="q"]');
    var panel = form.querySelector('[data-ac-ps-panel]');
    var list = form.querySelector('[data-ac-ps-list]');
    var status = form.querySelector('[data-ac-ps-status]');
    var footer = form.querySelector('[data-ac-ps-footer]');
    var viewAll = form.querySelector('[data-ac-ps-viewall]');
    var viewAllText = form.querySelector('[data-ac-ps-viewall-text]');
    var clearBtn = form.querySelector('[data-ac-ps-clear]');
    var live = form.querySelector('[data-ac-ps-live]');
    if (!input || !panel || !list) return;

    var listId = list.id || ('ac-ps-list-' + Math.random().toString(36).slice(2, 8));
    list.id = listId;
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', listId);
    input.setAttribute('aria-haspopup', 'listbox');

    var seq = 0, timer = null, controller = null, lastQuery = '', activeIndex = -1, rows = [];

    function open() { panel.hidden = false; input.setAttribute('aria-expanded', 'true'); form.classList.add('is-open'); }
    function close() {
      panel.hidden = true; input.setAttribute('aria-expanded', 'false'); form.classList.remove('is-open');
      setActive(-1); input.removeAttribute('aria-activedescendant');
    }
    function announce(msg) { if (!live) return; live.textContent = ''; window.setTimeout(function () { live.textContent = msg; }, 50); }

    function setState(name) {
      panel.setAttribute('data-state', name);
      if (status) {
        status.hidden = name === 'results';
        if (name === 'loading') status.innerHTML = '<span class="ac-ps__spinner" aria-hidden="true"></span><span>' + escapeHtml(panel.getAttribute('data-loading') || 'Searching…') + '</span>';
        else if (name === 'empty') status.innerHTML = '<span>' + escapeHtml((panel.getAttribute('data-empty') || 'No matches for “[query]”').replace('[query]', lastQuery)) + '</span>';
        else if (name === 'error') status.innerHTML = '<span>' + escapeHtml(panel.getAttribute('data-error') || 'Something went wrong. Try again.') + '</span>';
      }
      list.hidden = name !== 'results';
      if (footer) footer.hidden = name === 'loading' && !rows.length ? true : (name === 'error');
      updateClear();
    }
    function updateClear() { if (clearBtn) clearBtn.hidden = !input.value; }

    function setActive(i) {
      activeIndex = i;
      rows.forEach(function (r, k) {
        var on = k === i;
        r.classList.toggle('is-active', on);
        r.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) { input.setAttribute('aria-activedescendant', r.id); if (r.scrollIntoView) r.scrollIntoView({ block: 'nearest' }); }
      });
      if (i < 0) input.removeAttribute('aria-activedescendant');
    }

    function render(products) {
      list.innerHTML = '';
      rows = [];
      products.slice(0, LIMIT).forEach(function (p, i) {
        var li = document.createElement('li');
        li.className = 'ac-ps__row';
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.id = listId + '-' + i;
        var img = p.image || (p.featured_image && p.featured_image.url) || '';
        var cents = toCents(p.price_min != null ? p.price_min : p.price);
        var type = TYPE_LABEL[p.type] || p.type || '';
        li.innerHTML =
          '<a class="ac-ps__link" href="' + escapeHtml(p.url) + '" tabindex="-1">' +
            '<span class="ac-ps__thumb">' + (img ? '<img src="' + escapeHtml(resize(img, 144)) + '" alt="" width="72" height="72" decoding="async">' : '') + '</span>' +
            '<span class="ac-ps__text">' +
              '<span class="ac-ps__name">' + escapeHtml(shortName(p.title)) + '</span>' +
              (type ? '<span class="ac-ps__type">' + escapeHtml(type) + '</span>' : '') +
              (cents !== null && !isNaN(cents) ? '<span class="ac-ps__price">' + money(cents) + '</span>' : '') +
            '</span>' +
          '</a>';
        li.addEventListener('mousemove', function () { if (activeIndex !== i) setActive(i); });
        li.addEventListener('mouseleave', function () { if (activeIndex === i) setActive(-1); });
        list.appendChild(li);
        rows.push(li);
      });
      setActive(-1);
    }

    function updateViewAll(q) {
      var href = SEARCH_URL + '?q=' + encodeURIComponent(q) + '&type=product&options%5Bprefix%5D=last';
      if (viewAll) viewAll.setAttribute('href', href);
      if (viewAllText) {
        var tpl = viewAllText.getAttribute('data-template') || 'View all results for “[query]”';
        /* narrow screens: the short form, so a long query never overflows the button */
        var short = viewAllText.getAttribute('data-short') || 'View all results';
        viewAllText.textContent = (window.innerWidth < 480 && q.length > 6) ? short : tpl.replace('[query]', q);
      }
    }

    function request(q) {
      var mine = ++seq;
      if (controller) controller.abort();
      controller = (typeof window.AbortController === 'function') ? new window.AbortController() : null;
      var url = ENDPOINT + '?q=' + encodeURIComponent(q) +
        '&resources%5Btype%5D=product&resources%5Blimit%5D=' + LIMIT +
        '&resources%5Boptions%5D%5Bunavailable_products%5D=last' +
        '&resources%5Boptions%5D%5Bfields%5D=title%2Cproduct_type%2Cvariants.title%2Cvendor';
      window.fetch(url, { credentials: 'same-origin', signal: controller ? controller.signal : undefined })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (data) {
          if (mine !== seq) return;
          var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          products = products.filter(function (p) { return p.type !== 'options_price' && p.type !== 'PPLR_HIDDEN_PRODUCT'; });
          lastQuery = q;
          updateViewAll(q);
          if (!products.length) { rows = []; list.innerHTML = ''; setState('empty'); announce((panel.getAttribute('data-empty') || 'No matches for “[query]”').replace('[query]', q)); return; }
          render(products);
          setState('results');
          announce(products.length + ' ' + (products.length === 1 ? 'result' : 'results') + ' for ' + q);
        })
        .catch(function (e) {
          if (e && e.name === 'AbortError') return;
          if (mine !== seq) return;
          setState('error');
        });
    }

    function onInput() {
      var q = input.value.replace(/<[^>]*>/g, '').trim();
      updateClear();
      window.clearTimeout(timer);
      if (!q) { seq++; if (controller) controller.abort(); rows = []; list.innerHTML = ''; close(); return; }
      /* loading right away, on the first keystroke */
      if (panel.hidden || panel.getAttribute('data-state') !== 'results' || q !== lastQuery) {
        lastQuery = q; updateViewAll(q); setState('loading'); open();
      }
      timer = window.setTimeout(function () { request(q); }, DEBOUNCE);
    }

    input.addEventListener('input', onInput);
    input.addEventListener('focus', function () { if (input.value.trim() && rows.length) open(); });
    input.addEventListener('keydown', function (e) {
      if (panel.hidden) { if (e.key === 'ArrowDown' && input.value.trim()) { onInput(); } return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); if (rows.length) setActive((activeIndex + 1) % rows.length); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); if (rows.length) setActive((activeIndex - 1 + rows.length) % rows.length); }
      else if (e.key === 'Home' && rows.length) { e.preventDefault(); setActive(0); }
      else if (e.key === 'End' && rows.length) { e.preventDefault(); setActive(rows.length - 1); }
      else if (e.key === 'Enter') {
        if (activeIndex >= 0 && rows[activeIndex]) { e.preventDefault(); window.location.href = rows[activeIndex].querySelector('a').href; }
        /* otherwise the form submits to /search */
      }
      else if (e.key === 'Escape') { e.preventDefault(); close(); }
    });
    if (clearBtn) clearBtn.addEventListener('click', function () { input.value = ''; onInput(); input.focus(); });
    form.addEventListener('submit', function () { if (!input.value.trim()) { input.focus(); return false; } });

    document.addEventListener('click', function (e) { if (!form.contains(e.target)) close(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !panel.hidden) close(); });

    updateClear();
    form.acPredictive = { close: close, focus: function () { input.focus(); } };
  }

  function init() { document.querySelectorAll('form[data-ac-predictive]').forEach(setup); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);

  /* ---- phone sheet ------------------------------------------------------- */
  var sheet = null, lastFocus = null;
  function getSheet() { return sheet || (sheet = document.querySelector('[data-ac-search-sheet]')); }
  /* 2026-09-28 home package: an inline row under the header's top line that pushes
     the page down (no overlay, no scroll lock). The trigger toggles it and carries
     aria-expanded; Escape closes it and returns focus to the trigger. */
  function triggers() { return document.querySelectorAll('[data-ac-hd-search-trigger]'); }
  function setExpanded(on) { triggers().forEach(function (t) { t.setAttribute('aria-expanded', on ? 'true' : 'false'); }); }
  function openSheet(trigger) {
    var s = getSheet(); if (!s) return false;
    var line = (trigger && trigger.closest('.header__line-top')) || document.querySelector('.header__line-top');
    if (line && s.previousElementSibling !== line) line.parentNode.insertBefore(s, line.nextSibling);
    lastFocus = trigger || document.activeElement;
    s.classList.add('ac-search-sheet--inline');
    s.hidden = false;
    setExpanded(true);
    window.requestAnimationFrame(function () {
      s.classList.add('is-open');
      var f = s.querySelector('form[data-ac-predictive]'); setup(f);
      var i = s.querySelector('input[name="q"]'); if (i) i.focus();
    });
    return true;
  }
  function closeSheet() {
    var s = getSheet(); if (!s || s.hidden) return;
    s.classList.remove('is-open');
    var f = s.querySelector('form[data-ac-predictive]'); if (f && f.acPredictive) f.acPredictive.close();
    s.hidden = true;
    setExpanded(false);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-ac-hd-search-trigger]');
    if (t && window.innerWidth < 1025 && getSheet()) {
      e.preventDefault(); e.stopImmediatePropagation();
      if (getSheet().hidden) openSheet(t); else closeSheet();
      return;
    }
    if (e.target.closest && e.target.closest('[data-ac-search-sheet-close]')) { e.preventDefault(); closeSheet(); }
  }, true);
  document.addEventListener('keydown', function (e) { var s = getSheet(); if (e.key === 'Escape' && s && !s.hidden) closeSheet(); });
  window.acSearchSheet = { open: openSheet, close: closeSheet };
})();
